import React from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers";
import CommonDialog from "../CommonDialog";
import { CustomTextField2 } from "assets/styles/globalStyledComponent";
import dayjs from "dayjs";
import { IndianCurrency } from "app/utils/helper";

import { changeStatusOfIssuedBook } from "app/services/schoolReportingServices/library.service";
import TextFieldLabel from "../../TextFieldLabel";

const ReturnBookDialog = ({
  open = false,
  onClose = () => {},
  refetch = () => {},
  transaction = {},
}) => {
  // --- Handlers ---
  const formik = useFormik({
    initialValues: {
      returnedDate: null,
      fineAmount: transaction?.fineAmount || 0,
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      returnedDate: yup.date().required("Return date is required"),
      fineAmount: yup
        .number()
        .min(0, "Fine cannot be negative")
        .required("Fine amount is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      mutateReturnBook({
        bookTransactioDocId: transaction?._id,
        returnedDate: values.returnedDate
          ? dayjs(values.returnedDate).format("YYYY-MM-DD")
          : "",
        fineAmount: values.fineAmount,
      });
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const { mutate: mutateReturnBook, isLoading } = useMutation(
    changeStatusOfIssuedBook,
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message || "Book returned successfully");
        formik.resetForm();
        onClose();
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to return book");
      },
    }
  );

  // fine calculation
  const today = dayjs();
  const dueDate = transaction?.dueDate ? dayjs(transaction.dueDate) : null;
  const finePerDay = Number(transaction?.finePerDay) || 0;
  let suggestedFine = 0;
  if (dueDate && today.isAfter(dueDate)) {
    const overdueDays = today.diff(dueDate, "day");
    suggestedFine = overdueDays * finePerDay;
  }


  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title="Return Book"
      minWidth="500px"
      fullWidth
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextFieldLabel title="Book Name" />
              <Typography>{transaction?.bookTitle ?? "--"}</Typography>
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel title="Borrower Name" />
              <Typography>
                {transaction?.createdBy?.personName || "--"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel title="Issued Date" />
              <Typography>
                {transaction?.issuedDate
                  ? dayjs(transaction?.issuedDate).format("DD-MM-YYYY")
                  : "--"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel title="Due Date" />
              <Typography>
                {transaction?.dueDate
                  ? dayjs(transaction?.dueDate).format("DD-MM-YYYY")
                  : "--"}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel title="Fine / Day" />
              <Typography>
                {IndianCurrency(transaction?.finePerDay) + " / day" ?? "--"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextFieldLabel required title="Return Date" />
              <DatePicker
                minDate={dayjs(transaction?.issuedDate)}
                inputFormat="DD-MM-YYYY"
                // value={formik.values.dueDate}
                onChange={(date) => formik.setFieldValue("returnedDate", date)}
                renderInput={(params) => (
                  <CustomTextField2
                    {...params}
                    fullWidth
                    error={
                      formik.touched.returnedDate &&
                      Boolean(formik.errors.returnedDate)
                    }
                    helperText={
                      formik.touched.returnedDate && formik.errors.returnedDate
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextFieldLabel required title="Fine Amount" />
              <CustomTextField2
                fullWidth
                name="fineAmount"
                type="number"
                // value={formik.values.fineAmount}
                onChange={formik.handleChange}
                error={
                  formik.touched.fineAmount && Boolean(formik.errors.fineAmount)
                }
                helperText={
                  formik.touched.fineAmount && formik.errors.fineAmount
                }
                placeholder={suggestedFine > 0 ? `${suggestedFine}` : "0"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            loading={isLoading}
          >
            Return Book
          </Button>
        </DialogActions>
      </form>
    </CommonDialog>
  );
};

export default ReturnBookDialog;
